import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    Calendar,
    Clock,
    Loader2,
    Tag,
} from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import MarkdownBody from "@/components/MarkdownBody";
import FAQAccordion from "@/components/FAQAccordion";
import BlogStickyCta from "@/components/BlogStickyCta";
import {
    breadcrumbListSchema,
    faqPageSchema,
} from "@/lib/jsonld";
import { SITE_URL, SITE_NAME } from "@/lib/seo-constants";
import { TRACKS } from "@/lib/tracks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api/blog`;

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-CA", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return null;
    }
};

const articleSchema = (post, blogBase) => {
    const absoluteImage = post.cover_image
        ? (post.cover_image.startsWith("http")
            ? post.cover_image
            : `${SITE_URL}${post.cover_image}`)
        : `${SITE_URL}/og-share.jpg`;
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        image: [absoluteImage],
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        author: {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
        },
        publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/favicon-512.png`,
            },
        },
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_URL}${blogBase}/${post.slug}`,
        },
        articleSection: post.category,
        keywords: (post.tags || []).join(", "),
    };
};

const NotFoundCard = ({ blogPath = "/blog" }) => (
    <Section className="py-24 md:py-32 min-h-[60vh] flex items-center" bg="white" data-testid="blog-post-not-found">
        <Container size="narrow">
            <div className="bg-brand-offwhite border border-brand-dark/10 p-10 text-center">
                <AlertCircle className="h-10 w-10 text-brand-coral mx-auto mb-4" strokeWidth={2} />
                <h1 className="font-display text-4xl tracking-wide text-brand-dark leading-tight mb-3">
                    Post not found.
                </h1>
                <p className="text-base text-brand-dark/70 mb-6">
                    The article you're looking for has been moved or unpublished.
                </p>
                <BrandButton asChild variant="primary" size="md">
                    <Link to={blogPath} data-testid="blog-post-cta-back">
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                        Back to all posts
                    </Link>
                </BrandButton>
            </div>
        </Container>
    </Section>
);

const RelatedServicesBlock = ({ slugs, track }) => {
    if (!Array.isArray(slugs) || slugs.length === 0) return null;
    const items = track.services.filter((s) =>
        slugs.includes(s.path.split("/").pop()),
    );
    if (items.length === 0) return null;
    return (
        <div className="mt-12 pt-8 border-t border-brand-dark/10" data-testid="blog-post-related">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-4">
                Related Services
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((s) => (
                    <Link
                        key={s.path}
                        to={s.path}
                        className="group flex items-center justify-between bg-brand-offwhite border border-brand-dark/10 hover:border-brand-cyan transition-colors px-5 py-4"
                        data-testid={`blog-post-related-${s.path.split("/").pop()}`}
                    >
                        <span className="font-sans font-semibold text-sm text-brand-dark group-hover:text-brand-cyan transition-colors">
                            {s.label}
                        </span>
                        <ArrowRight className="h-4 w-4 text-brand-gray group-hover:text-brand-cyan transition-all group-hover:translate-x-1" strokeWidth={2.25} />
                    </Link>
                ))}
            </div>
        </div>
    );
};

const BlogPost = ({ trackKey = "residential" }) => {
    const track = TRACKS[trackKey];
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [status, setStatus] = useState("loading"); // 'loading' | 'ready' | 'notfound' | 'error'
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let active = true;
        setStatus("loading");
        setPost(null);
        const load = async () => {
            try {
                const res = await axios.get(`${API_BASE}/${slug}`);
                if (!active) return;
                setPost(res.data);
                setStatus("ready");
            } catch (err) {
                if (!active) return;
                if (err?.response?.status === 404) {
                    setStatus("notfound");
                } else {
                    setErrorMessage(
                        err?.response?.data?.detail ||
                            "Could not load this post. Try refreshing.",
                    );
                    setStatus("error");
                }
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [slug]);

    if (status === "loading") {
        return (
            <Section className="py-32 min-h-[60vh]" bg="white" data-testid="blog-post-loading">
                <Container>
                    <div className="flex items-center justify-center">
                        <Loader2 className="h-8 w-8 text-brand-cyan animate-spin" strokeWidth={2} />
                    </div>
                </Container>
            </Section>
        );
    }

    if (status === "notfound") return <NotFoundCard blogPath={track.blog} />;

    if (status === "error") {
        return (
            <Section className="py-24" bg="white" data-testid="blog-post-error">
                <Container size="narrow">
                    <div className="border-l-4 border-brand-coral bg-brand-coral/10 p-5 text-sm text-brand-dark">
                        {errorMessage}
                    </div>
                </Container>
            </Section>
        );
    }

    const dateLabel = formatDate(post.published_at);
    const hasFaqs = Array.isArray(post.faqs) && post.faqs.length > 0;

    const jsonLd = [
        breadcrumbListSchema([
            ...(trackKey === "residential"
                ? []
                : [{ name: track.label, path: track.home }]),
            { name: "Blog", path: track.blog },
            { name: post.title, path: `${track.blog}/${post.slug}` },
        ]),
        articleSchema(post, track.blog),
    ];
    if (hasFaqs) jsonLd.push(faqPageSchema(post.faqs));

    return (
        <>
            <Seo
                title={`${post.title} — Reformity Inc.`}
                description={post.excerpt}
                path={`${track.blog}/${post.slug}`}
                image={post.cover_image || undefined}
                jsonLd={jsonLd}
                preloadImage={post.cover_image || null}
            />

            <BlogStickyCta
                category={post.category}
                slug={post.slug}
                contactPath={track.contact}
            />

            {/* Hero */}
            <Section dark className="py-12 md:py-16" data-testid="blog-post-hero">
                <Container size="narrow">
                    <nav
                        className="text-xs uppercase tracking-wider font-semibold text-white/60 mb-6 flex items-center gap-2 flex-wrap"
                        aria-label="Breadcrumb"
                        data-testid="blog-post-breadcrumb"
                    >
                        <Link to={track.home} className="hover:text-brand-cyan transition-colors">
                            {track.label}
                        </Link>
                        <span aria-hidden="true">/</span>
                        <Link to={track.blog} className="hover:text-brand-cyan transition-colors">
                            Blog
                        </Link>
                        <span aria-hidden="true">/</span>
                        <span className="text-brand-cyan">{post.category}</span>
                    </nav>

                    <div className="flex items-center gap-3 mb-5 text-[10px] uppercase tracking-wider font-semibold">
                        <span className="bg-brand-coral text-white px-2 py-1" data-testid="blog-post-category">
                            {post.category}
                        </span>
                        {dateLabel && (
                            <span className="flex items-center gap-1 text-white/70">
                                <Calendar className="h-3 w-3" strokeWidth={2.25} />
                                {dateLabel}
                            </span>
                        )}
                        <span className="flex items-center gap-1 text-white/70">
                            <Clock className="h-3 w-3" strokeWidth={2.25} />
                            {post.reading_time_min} min read
                        </span>
                    </div>

                    <h1
                        className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide leading-tight text-white"
                        data-testid="blog-post-title"
                    >
                        {post.title}
                    </h1>

                    <p className="mt-5 max-w-2xl text-base md:text-lg text-white/75 font-light italic leading-relaxed" data-testid="blog-post-excerpt">
                        {post.excerpt}
                    </p>
                </Container>
            </Section>

            {/* Cover image */}
            {post.cover_image && (
                <div className="bg-brand-white" data-testid="blog-post-cover-wrapper">
                    <Container className="-mt-8 md:-mt-12">
                        <img
                            src={post.cover_image}
                            alt={post.cover_image_alt || post.title}
                            className="w-full aspect-[16/9] object-cover shadow-lg border border-brand-dark/10"
                            data-testid="blog-post-cover"
                        />
                    </Container>
                </div>
            )}

            {/* Body */}
            <Section className="py-12 md:py-20 pb-28 md:pb-20" bg="white" data-testid="blog-post-body-section">
                <Container size="narrow">
                    <article>
                        <MarkdownBody
                            markdown={post.body_markdown || ""}
                            testid="blog-post-body"
                        />

                        {Array.isArray(post.tags) && post.tags.length > 0 && (
                            <div className="mt-12 flex items-center flex-wrap gap-2" data-testid="blog-post-tags">
                                <Tag className="h-4 w-4 text-brand-gray" strokeWidth={2.25} />
                                {post.tags.map((t) => (
                                    <span
                                        key={t}
                                        className="text-[10px] uppercase tracking-wider font-semibold bg-brand-offwhite text-brand-dark/80 px-2 py-1 border border-brand-dark/10"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        )}

                        <RelatedServicesBlock slugs={post.related_services} track={track} />
                    </article>
                </Container>
            </Section>

            {/* FAQs */}
            {hasFaqs && (
                <Section className="py-16 md:py-20" data-testid="blog-post-faq-section">
                    <Container size="narrow">
                        <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                            Frequently Asked
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-brand-dark leading-tight mb-8">
                            Quick answers.
                        </h2>
                        <FAQAccordion items={post.faqs} testidPrefix="blog-post-faq" />
                    </Container>
                </Section>
            )}

            {/* CTA band */}
            <Section dark className="py-16 md:py-20" data-testid="blog-post-cta-band">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                                Ready to start?
                            </p>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide text-white leading-none">
                                Get a free quote.
                            </h2>
                            <p className="mt-5 text-base text-white/70 max-w-xl">
                                We measure, sketch, and price your project in writing within
                                48 hours. Serving the GTA from North York.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                            <BrandButton
                                asChild
                                variant="primary"
                                size="lg"
                                className="w-full lg:w-auto"
                            >
                                <Link to={track.contact} data-testid="blog-post-cta-quote">
                                    Start My Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                            <BrandButton
                                asChild
                                variant="outline"
                                tone="onDark"
                                size="md"
                                className="w-full lg:w-auto"
                            >
                                <Link to={track.blog} data-testid="blog-post-cta-back">
                                    <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                                    All articles
                                </Link>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
};

export default BlogPost;
